import { hasPremiumFeature, PremiumFeatures } from '@lub0v/santa-utils';
import { boxSubdomain } from '@lub0v/santa-utils';
import { parseGamePath } from './routes';

export function configuredBrowserBoxDomains(): string[] {
  if (typeof window === 'undefined') return [];
  return (
    window.__ssBoxDomains ??
    import.meta.env.VITE_PREMIUM_BOX_BASE_DOMAINS?.split(',').map((base: string) =>
      base.trim().toLowerCase(),
    ) ??
    (import.meta.env.DEV &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '[::1]' ||
      window.location.hostname.endsWith('.localhost'))
      ? ['santa.localhost']
      : [])
  );
}

export function mainSiteUrl(path = '/'): string {
  if (typeof window === 'undefined') return path;
  const base = configuredBrowserBoxDomains().find((base) =>
    boxSubdomain(window.location.hostname, [base]),
  );
  if (!base) return path;
  const url = new URL(path, window.location.origin);
  url.hostname = base;
  return url.href;
}

/** Only eligible, active premium games leave the main site's origin. */
export function premiumGameUrl(box: {
  key: string;
  premiumKey?: string | null;
  isPremium?: boolean;
    features?: PremiumFeatures;
  isArchived?: boolean;
}): string {
  const fallback = `/box/${encodeURIComponent(box.key)}`;
  if (typeof window === 'undefined' || !box.premiumKey || !hasPremiumFeature(box, 'boxDomain') || box.isArchived)
    return fallback;
  const base = configuredBrowserBoxDomains().find(
    (base) =>
      window.location.hostname === base ||
      window.location.hostname === `www.${base}` ||
      boxSubdomain(window.location.hostname, [base]) ||
      (import.meta.env.DEV &&
        ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname) &&
        base.endsWith('.localhost')),
  );
  if (!base || boxSubdomain(`${box.premiumKey}.${base}`, [base]) === null) return fallback;
  const url = new URL('/', window.location.origin);
  url.hostname = `${box.premiumKey.toLowerCase()}.${base}`;
  return url.href;
}

export function isGameDomainPath(pathname: string, key: string): boolean {
  // Settings embeds this same-origin route and exchanges the draft via postMessage.
  return (
    pathname === '/theme-preview' ||
    pathname === '/notifications' ||
    pathname === '/open' ||
    pathname === '/open/recover' ||
    parseGamePath(pathname, key)?.key === key
  );
}

/** Return only to game pages on an explicitly configured sibling domain. */
export function safeGameReturnUrl(value: string | null): string | null {
  if (!value || typeof window === 'undefined') return null;
  try {
    const url = new URL(value);
    const label = boxSubdomain(url.hostname, configuredBrowserBoxDomains());
    if (
      !label ||
      url.protocol !== window.location.protocol ||
      url.port !== window.location.port ||
      url.username ||
      url.password
    )
      return null;
    const route = url.pathname.startsWith('/box/') ? null : parseGamePath(url.pathname, 'game');
    return url.pathname === '/' || url.pathname === '/notifications' || route?.key === 'game'
      ? url.href
      : null;
  } catch {
    return null;
  }
}

/** Only known domain bases can supply a redirect destination. */
export function subdomainMainSiteUrl(): string | null {
  if (typeof window === 'undefined' || window.__ssIsPreview) return null;
  const host = window.location.hostname.toLowerCase().replace(/\.$/, '');
  const base = configuredBrowserBoxDomains().find(
    (base) => host.endsWith(`.${base}`) && host !== `www.${base}`,
  );
  if (!base) return null;
  const url = new URL('/', window.location.origin);
  url.hostname = host.startsWith('com-preview-') && base === 'santa-secret.ru'
    ? 'santa-secret.com'
    : base;
  return url.href;
}
